class MixAndMatchSet extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const configEl = this.querySelector("[data-mix-and-match-config]");
    this.config = configEl ? JSON.parse(configEl.textContent) : {};
    this.requiredUnits = this.config.requiredUnits || parseInt(this.dataset.requiredUnits, 10) || 20;

    this.tabs = Array.from(this.querySelectorAll("[data-size-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-size-panel]"));
    this.progressTextEl = this.querySelector("[data-progress-text]");
    this.trackerStatusEl = this.querySelector("[data-tracker-status]");
    this.barEl = this.querySelector("[data-bar]");
    this.atcBtn = this.querySelector("[data-atc]");
    this.atcLabelEl = this.querySelector("[data-atc-label]");
    this.priceEl = this.querySelector("[data-prod-price]");
    this._defaultAtcLabel = this.atcLabelEl
      ? this.atcLabelEl.textContent.trim()
      : "Add to cart";
    this.editingBindId = null;
    this.editingQtyByVariantId = new Map();

    const currency = this.config.currencyCode || (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";
    const locale = (window.Shopify && Shopify.locale) || undefined;
    // narrowSymbol → "$294.00" for AUD (theme already shows "AUD" beside the price)
    this._currencyFormatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol"
    });

    this.stateByPanel = new Map();
    this.cartQtyByVariantId = new Map();
    this.activePanelKey = this._getActivePanelKey();
    this.colourTabs = Array.from(this.querySelectorAll("[data-colour-tab]"));
    this.activeColourTab = "all";

    this._initTabs();
    this._initColourTabs();
    this._initPanels();
    this._bindEvents();
    this._bindCartListener();
    this._ensurePanelState(this.activePanelKey);
    this._applyColourFilter(this.activeColourTab);
    this._render();

    const isEditingFromUrl = Boolean(this._getBundleParam());
    if (isEditingFromUrl) {
      this._showPageOverlay("Loading your bundle…");
    }

    this._refreshFromCart({ reset: true })
      .then(() => this._maybeHydrateFromBundleParam())
      .finally(() => {
        if (isEditingFromUrl) this._hidePageOverlay();
      });
  }

  _ensurePageOverlay() {
    if (this._pageOverlayEl && document.body.contains(this._pageOverlayEl)) {
      return this._pageOverlayEl;
    }

    const overlay = document.createElement("div");
    overlay.className = "jsc-mm-page-overlay";
    overlay.hidden = true;
    overlay.setAttribute("aria-live", "polite");
    overlay.setAttribute("aria-busy", "true");
    overlay.innerHTML = '<p class="jsc-mm-page-overlay__text" data-mm-overlay-text></p>';
    document.body.appendChild(overlay);
    this._pageOverlayEl = overlay;
    return overlay;
  }

  _showPageOverlay(message) {
    const overlay = this._ensurePageOverlay();
    const textEl = overlay.querySelector("[data-mm-overlay-text]");
    if (textEl) textEl.textContent = message || "";
    overlay.hidden = false;
    document.body.classList.add("jsc-mm-page-overlay-open");
  }

  _hidePageOverlay() {
    if (!this._pageOverlayEl) return;
    this._pageOverlayEl.hidden = true;
    document.body.classList.remove("jsc-mm-page-overlay-open");
  }

  _getActivePanelKey() {
    const activePanel = this.querySelector(".mix-and-match__panel.is-active");
    return activePanel ? activePanel.dataset.sizePanel : "default";
  }

  _getBundleParam() {
    try {
      return new URLSearchParams(window.location.search).get("bundle");
    } catch (error) {
      return null;
    }
  }

  _initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this._activateSizePanel(tab.dataset.sizeTab);
      });
    });
  }

  _activateSizePanel(sizeKey) {
    if (!sizeKey) return;
    this.tabs.forEach((t) => {
      const isActive = t.dataset.sizeTab === sizeKey;
      t.classList.toggle("is-active", isActive);
      t.setAttribute("aria-selected", isActive ? "true" : "false");
    });
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.sizePanel === sizeKey;
      panel.classList.toggle("is-active", isActive);
      panel.hidden = !isActive;
    });
    this.activePanelKey = sizeKey;
    this._ensurePanelState(sizeKey);
    this._applyColourFilter(this.activeColourTab);
    this._render();
  }

  _initColourTabs() {
    this.colourTabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this._activateColourTab(tab.dataset.colourTab);
      });
    });
  }

  _activateColourTab(colourKey) {
    if (!colourKey) return;
    this.activeColourTab = colourKey;
    this.colourTabs.forEach((t) => {
      const isActive = t.dataset.colourTab === colourKey;
      t.classList.toggle("is-active", isActive);
      t.setAttribute("aria-selected", isActive ? "true" : "false");
    });
    this._applyColourFilter(colourKey);
  }

  _applyColourFilter(colourKey) {
    const key = (colourKey || "all").trim().toLowerCase();
    const panel = this.panels.find((p) => p.dataset.sizePanel === this.activePanelKey)
      || this.querySelector(".mix-and-match__panel.is-active");
    if (!panel) return;

    panel.querySelectorAll(".mix-and-match__card").forEach((card) => {
      if (key === "all") {
        card.hidden = false;
        return;
      }
      const labels = (card.dataset.colourLabels || "")
        .split("||")
        .map((label) => label.trim().toLowerCase())
        .filter(Boolean);
      card.hidden = !labels.includes(key);
    });
  }

  _initPanels() {
    this.panels.forEach((panel) => {
      const key = panel.dataset.sizePanel;
      if (!this.stateByPanel.has(key)) {
        this.stateByPanel.set(key, this._buildInitialState(panel));
      }
    });
  }

  _buildInitialState(panel) {
    const cards = Array.from(panel.querySelectorAll(".mix-and-match__card"));
    const qty = cards.map(() => 0);

    if (this.config.preselectBundle && !this.editingBindId) {
      let slotsFilled = 0;
      cards.forEach((card, index) => {
        const available = card.dataset.available === "true";
        if (available && this._inventoryCap(card) > 0 && slotsFilled < this.requiredUnits) {
          qty[index] = 1;
          slotsFilled += 1;
        }
      });
    }

    return { qty, cards };
  }

  _resetPanels() {
    this.stateByPanel.clear();
    this._initPanels();
    this._ensurePanelState(this.activePanelKey);
    this._syncDepletedUi();
  }

  _clearEditMode() {
    this.editingBindId = null;
    this.editingQtyByVariantId = new Map();
  }

  _syncCartQuantities(cart) {
    const next = new Map();
    (cart?.items || []).forEach((item) => {
      const id = item.id || item.variant_id;
      if (!id) return;
      next.set(id, (next.get(id) || 0) + (item.quantity || 0));
    });
    this.cartQtyByVariantId = next;
  }

  _syncEditingQuantities(cart, bindId) {
    const next = new Map();
    if (!bindId) {
      this.editingQtyByVariantId = next;
      return;
    }
    (cart?.items || []).forEach((item) => {
      const props = item.properties || {};
      if (props._bindId !== bindId) return;
      const id = item.id || item.variant_id;
      if (!id) return;
      next.set(id, (next.get(id) || 0) + (item.quantity || 0));
    });
    this.editingQtyByVariantId = next;
  }

  _clampAllPanelQtys() {
    this.stateByPanel.forEach((state) => {
      state.cards.forEach((card, index) => {
        const cap = this._inventoryCap(card);
        if (state.qty[index] > cap) state.qty[index] = cap;
      });
    });
  }

  _syncDepletedUi() {
    this.panels.forEach((panel) => {
      panel.querySelectorAll(".mix-and-match__card").forEach((card) => {
        if (card.dataset.available !== "true") return;
        const depleted = this._inventoryCap(card) <= 0;
        card.classList.toggle("mix-and-match__card--oos", depleted);
        card.classList.toggle("mix-and-match__card--cart-depleted", depleted);
      });
    });
  }

  async _refreshFromCart({ reset = false, cart = null } = {}) {
    try {
      const cartData = cart || await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      this._syncCartQuantities(cartData);

      if (this.editingBindId) {
        const stillInCart = (cartData.items || []).some(
          (item) => (item.properties || {})._bindId === this.editingBindId
        );
        if (!stillInCart) {
          this._clearEditMode();
        } else {
          this._syncEditingQuantities(cartData, this.editingBindId);
        }
      }

      if (reset && !this.editingBindId) {
        this._resetPanels();
      } else {
        this._clampAllPanelQtys();
        this._syncDepletedUi();
      }
      this._render();
      return cartData;
    } catch (error) {
      console.error("Mix and Match cart sync failed", error);
      return null;
    }
  }

  _bindCartListener() {
    this._onCartChanged = (event) => {
      const cart = event?.detail?.cart || null;
      // Keep builder selection while editing a bind group from ?bundle=
      this._refreshFromCart({ reset: !this.editingBindId, cart });
    };
    // Normal line-item updates dispatch cart:change on <html>.
    document.documentElement.addEventListener("cart:change", this._onCartChanged);
    // Bundle remove (removeBindGwp) dispatches cart:refresh on document only.
    document.addEventListener("cart:refresh", this._onCartChanged);
  }

  _ensurePanelState(panelKey) {
    if (!this.stateByPanel.has(panelKey)) {
      const panel = this.panels.find((p) => p.dataset.sizePanel === panelKey);
      if (panel) {
        this.stateByPanel.set(panelKey, this._buildInitialState(panel));
      }
    }
  }

  _getActiveState() {
    return this.stateByPanel.get(this.activePanelKey);
  }

  _total(state) {
    return state.qty.reduce((sum, n) => sum + n, 0);
  }

  _subtotalCents(state) {
    return state.cards.reduce((sum, card, index) => {
      const qty = state.qty[index] || 0;
      const price = parseInt(card.dataset.price, 10) || 0;
      return sum + qty * price;
    }, 0);
  }

  _discountPercent() {
    const key = this.activePanelKey;
    const discounts = this.config.bundleDiscountsBySize || {};
    let raw = discounts[key];

    if (raw === null || raw === undefined || raw === "") {
      const variants = this.config.bundleVariants || [];
      const match = key === "default"
        ? variants.find((v) => v.discount !== null && v.discount !== undefined && v.discount !== "") || variants[0]
        : variants.find((v) =>
            v.title === key || v.option1 === key || v.option2 === key || v.option3 === key
          );
      raw = match ? match.discount : "";
    }

    if (raw === null || raw === undefined || raw === "") return 0;
    const parsed = parseFloat(String(raw).replace(/%/g, "").trim());
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }

  _matchedBundleDiscount() {
    const key = this.activePanelKey;
    const discounts = this.config.bundleDiscountsBySize || {};
    if (discounts[key] !== null && discounts[key] !== undefined && discounts[key] !== "") {
      return discounts[key];
    }
    const variants = this.config.bundleVariants || [];
    const match = key === "default"
      ? variants.find((v) => v.discount !== null && v.discount !== undefined && v.discount !== "") || variants[0]
      : variants.find((v) =>
          v.title === key || v.option1 === key || v.option2 === key || v.option3 === key
        );
    return match ? match.discount : "";
  }

  _formatMoney(cents) {
    return this._currencyFormatter.format(cents / 100);
  }

  _updatePrice(state) {
    if (!this.priceEl) return;

    const subtotal = this._subtotalCents(state);
    const percent = this._discountPercent();
    const saleTotal = percent > 0
      ? Math.round(subtotal * (1 - percent / 100))
      : subtotal;
    const onSale = percent > 0 && saleTotal < subtotal;

    const compareAt = this.priceEl.querySelector("[data-compare-at]");
    const compareAtAmount = this.priceEl.querySelector("[data-compare-at-amount]");
    const salePrice = this.priceEl.querySelector("[data-sale-price]");
    const saleAmount = this.priceEl.querySelector("[data-sale-amount]");
    const currencyEl = this.priceEl.querySelector("[data-currency]");

    if (compareAt) {
      compareAt.hidden = !onSale;
      if (compareAtAmount) compareAtAmount.textContent = this._formatMoney(subtotal);
    }

    if (saleAmount) saleAmount.textContent = this._formatMoney(saleTotal);

    if (salePrice) {
      salePrice.classList.toggle("text-on-sale", onSale);
      salePrice.classList.toggle("text-base", true);
    }

    if (currencyEl) {
      currencyEl.classList.toggle("jsc-red-color", onSale);
    }
  }

  _inventoryCap(card) {
    if (card.dataset.inventoryManagement !== "shopify") return Infinity;
    if (card.dataset.inventoryPolicy === "continue") return Infinity;
    const stock = parseInt(card.dataset.inventoryQuantity, 10);
    if (!Number.isFinite(stock)) return Infinity;
    const variantId = parseInt(card.dataset.variantId, 10);
    const inCart = this.cartQtyByVariantId.get(variantId) || 0;
    const editingQty = this.editingBindId
      ? (this.editingQtyByVariantId.get(variantId) || 0)
      : 0;
    return Math.max(0, stock - (inCart - editingQty));
  }

  _maxQtyForCard(state, index) {
    const card = state.cards[index];
    if (!card) return 0;
    const cap = this._inventoryCap(card);
    if (this.config.allowOverRequiredUnits) return cap;
    const currentQty = state.qty[index] || 0;
    const total = this._total(state);
    const remaining = this.requiredUnits - (total - currentQty);
    return Math.min(cap, Math.max(0, remaining));
  }

  _commitQtyInput(input) {
    const card = input.closest(".mix-and-match__card");
    if (!card || card.classList.contains("mix-and-match__card--oos")) return;

    const state = this._getActiveState();
    if (!state) return;

    const index = state.cards.indexOf(card);
    if (index === -1) return;

    const parsed = parseInt(input.value, 10);
    const nextQty = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const maxQty = this._maxQtyForCard(state, index);
    state.qty[index] = Math.min(maxQty, Math.max(0, nextQty));
    this._render();
  }

  _replaceCount(text, count) {
    return String(text || "").replace(/\[count\]/g, String(count));
  }

  _generateBindId() {
    const now = new Date();
    const dateTime = [
      now.getDate(),
      now.getMonth() + 1,
      now.getFullYear()
    ].join("/") + "," + [
      now.getHours(),
      now.getMinutes(),
      now.getSeconds()
    ].join(":");
    return `mix-and-match,${dateTime}`;
  }

  _itemSizeValue(item) {
    const optionName = this.config.sizeOptionName || "Size";
    const options = item.options_with_values || [];
    if (options.length) {
      const match = options.find((opt) => opt.name === optionName);
      if (match && match.value) return match.value;
    }
    // cart.js usually exposes option names via item.options (values only) + product options unavailable
    // Fall back to common Size being option1 or matching a known panel key
    const values = item.options || [];
    const panelKeys = this.panels.map((p) => p.dataset.sizePanel).filter((k) => k && k !== "default");
    for (const value of values) {
      if (panelKeys.includes(value)) return value;
    }
    return values[0] || null;
  }

  async _maybeHydrateFromBundleParam() {
    const bindId = this._getBundleParam();
    if (!bindId) return;

    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      const bundleItems = (cart.items || []).filter(
        (item) => (item.properties || {})._bindId === bindId
      );
      if (bundleItems.length === 0) return;

      this.editingBindId = bindId;
      this._syncCartQuantities(cart);
      this._syncEditingQuantities(cart, bindId);

      const sizeValue = this._itemSizeValue(bundleItems[0]);
      if (sizeValue) {
        const matchingPanel = this.panels.find((p) => p.dataset.sizePanel === sizeValue);
        if (matchingPanel) {
          this._activateSizePanel(sizeValue);
        }
      }

      this._ensurePanelState(this.activePanelKey);
      const state = this._getActiveState();
      if (!state) return;

      const qtyByVariant = new Map();
      bundleItems.forEach((item) => {
        const id = item.id || item.variant_id;
        if (!id) return;
        qtyByVariant.set(id, (qtyByVariant.get(id) || 0) + (item.quantity || 0));
      });

      state.cards.forEach((card, index) => {
        const variantId = parseInt(card.dataset.variantId, 10);
        state.qty[index] = qtyByVariant.get(variantId) || 0;
      });

      this._clampAllPanelQtys();
      this._syncDepletedUi();
      this._render();
    } catch (error) {
      console.error("Mix and Match hydrate failed", error);
    }
  }

  _clearBundleParamFromUrl() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("bundle")) return;
      url.searchParams.delete("bundle");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    } catch (error) {
      // ignore
    }
  }

  async _removeBindGroup(bindId) {
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    const updates = {};
    (cart.items || []).forEach((item) => {
      if ((item.properties || {})._bindId === bindId) {
        updates[item.key] = 0;
      }
    });
    if (Object.keys(updates).length === 0) return cart;

    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  _bindEvents() {
    this.addEventListener("click", (event) => {
      const inc = event.target.closest("[data-inc]");
      const dec = event.target.closest("[data-dec]");
      if (!inc && !dec) return;

      const card = event.target.closest(".mix-and-match__card");
      if (!card || card.classList.contains("mix-and-match__card--oos")) return;

      const state = this._getActiveState();
      if (!state) return;

      const index = state.cards.indexOf(card);
      if (index === -1) return;

      const total = this._total(state);
      const cap = this._inventoryCap(card);

      if (inc && !inc.disabled) {
        const underCap = state.qty[index] < cap;
        const underRequired = total < this.requiredUnits;
        if (underCap && (this.config.allowOverRequiredUnits || underRequired)) {
          state.qty[index] += 1;
          this._render();
        }
        return;
      }

      if (dec && !dec.disabled) {
        if (state.qty[index] > 0) {
          state.qty[index] -= 1;
          this._render();
        }
      }
    });

    this.addEventListener("input", (event) => {
      const input = event.target.closest("input[data-qty]");
      if (!input) return;
      // Soft-filter non-digits while typing; hard clamp happens on commit.
      input.value = String(input.value || "").replace(/[^\d]/g, "");
    });

    this.addEventListener("change", (event) => {
      const input = event.target.closest("input[data-qty]");
      if (!input) return;
      this._commitQtyInput(input);
    });

    this.addEventListener("blur", (event) => {
      const input = event.target.closest("input[data-qty]");
      if (!input) return;
      this._commitQtyInput(input);
    }, true);

    this.addEventListener("keydown", (event) => {
      const input = event.target.closest("input[data-qty]");
      if (!input) return;
      if (event.key === "Enter") {
        event.preventDefault();
        input.blur();
      }
    });

    if (this.atcBtn) {
      this.atcBtn.addEventListener("click", () => this._addToCart());
    }
  }

  _render() {
    const state = this._getActiveState();
    if (!state) return;

    const total = this._total(state);

    state.cards.forEach((card, index) => {
      const qty = state.qty[index];
      const available = card.dataset.available === "true";
      const badge = card.querySelector("[data-qbadge]");
      const qtyEl = card.querySelector("[data-qty]");
      const extraEl = card.querySelector("[data-extra]");
      const incBtn = card.querySelector("[data-inc]");
      const decBtn = card.querySelector("[data-dec]");

      if (!available) return;

      if (badge) {
        badge.textContent = qty;
        badge.hidden = qty <= 0;
        badge.classList.toggle("mix-and-match__qbadge--extra", qty > 0);
      }

      const maxQty = this._maxQtyForCard(state, index);
      if (qtyEl) {
        if (qtyEl.tagName === "INPUT") {
          qtyEl.min = "0";
          if (Number.isFinite(maxQty)) {
            qtyEl.max = String(maxQty);
          } else {
            qtyEl.removeAttribute("max");
          }
          if (document.activeElement !== qtyEl) {
            qtyEl.value = String(qty);
          }
        } else {
          qtyEl.textContent = qty;
        }
      }

      const hasExtra = qty > 0;
      card.classList.toggle("mix-and-match__card--extra", hasExtra);

      if (extraEl) {
        if (hasExtra) {
          extraEl.textContent = this._replaceCount(this.config.extraLabel, qty - 1);
          extraEl.hidden = false;
        } else {
          extraEl.hidden = true;
        }
      }

      const cap = this._inventoryCap(card);
      if (incBtn) {
        const atRequiredCap = !this.config.allowOverRequiredUnits && total >= this.requiredUnits;
        incBtn.disabled = atRequiredCap || qty >= cap;
      }
      if (decBtn) {
        decBtn.disabled = qty <= 0;
      }
    });

    if (this.progressTextEl) {
      const percent = this._discountPercent();
      const percentLabel = Number.isFinite(percent) ? String(percent).replace(/\.0$/, "") : "0";
      if (total < this.requiredUnits) {
        const remaining = this.requiredUnits - total;
        this.progressTextEl.textContent = `${total} of ${this.requiredUnits} · ${remaining} more to unlock ${percentLabel}% off`;
      } else if (total > this.requiredUnits && this.config.allowOverRequiredUnits) {
        this.progressTextEl.textContent = `${percentLabel}% off applied — add as many more as you like`;
      } else {
        this.progressTextEl.textContent = `${total} of ${this.requiredUnits} · ${percentLabel}% off applied`;
      }
    }
    if (this.trackerStatusEl) {
      const percent = this._discountPercent();
      const percentLabel = Number.isFinite(percent) ? String(percent).replace(/\.0$/, "") : "0";
      if (total > this.requiredUnits && this.config.allowOverRequiredUnits) {
        this.trackerStatusEl.textContent = `${total} bottles · ${percentLabel}% off applied`;
      } else if (total < this.requiredUnits) {
        const remaining = this.requiredUnits - total;
        const help = this._replaceCount(
          this.config.inProgressText || "Add [count] more bottle(s) to complete your set.",
          remaining
        );
        this.trackerStatusEl.textContent = `Your set ${total}/${this.requiredUnits} — ${help}`;
      } else {
        const complete = this.config.completeText || "Set complete";
        this.trackerStatusEl.textContent = `Your set ${total}/${this.requiredUnits} — ${complete}`;
      }
    }
    if (this.barEl) {
      this.barEl.style.width = `${Math.min(100, (total / this.requiredUnits) * 100)}%`;
    }
    if (this.atcBtn) {
      const bundleAvailable = this.config.bundleAvailable !== false;
      const meetsRequired = this.config.allowOverRequiredUnits
        ? total >= this.requiredUnits
        : total === this.requiredUnits;
      this.atcBtn.disabled = !bundleAvailable || !meetsRequired;
      if (this.atcLabelEl) {
        this.atcLabelEl.textContent = this.editingBindId
          ? (this.config.updateBundleLabel || "Update Bundle")
          : this._defaultAtcLabel;
      }
    }

    this._updatePrice(state);
  }

  async _addToCart() {
    const state = this._getActiveState();
    const total = state ? this._total(state) : 0;
    const meetsRequired = this.config.allowOverRequiredUnits
      ? total >= this.requiredUnits
      : total === this.requiredUnits;
    if (!state || !meetsRequired) return;
    if (this.config.bundleAvailable === false) return;

    const editingBindId = this.editingBindId;
    const bindId = this._generateBindId();
    const bundleDiscount = this._matchedBundleDiscount() ?? "";
    const items = [];
    state.cards.forEach((card, index) => {
      const qty = state.qty[index];
      if (qty > 0) {
        items.push({
          id: parseInt(card.dataset.variantId, 10),
          quantity: qty,
          properties: {
            _Bundle: "Mix-and-match",
            _bindId: bindId,
            "_main-product-image": this.config.mainProductImage || "",
            "_main-product-title": this.config.mainProductTitle || "",
            "_main-product-url": this.config.mainProductUrl || "",
            "_main-product-handle": this.config.mainProductHandle || "",
            _bundle_discount: String(bundleDiscount)
          }
        });
      }
    });

    if (items.length === 0) return;

    this.atcBtn.setAttribute("disabled", "disabled");
    this.atcBtn.setAttribute("aria-busy", "true");
    if (editingBindId) {
      this._showPageOverlay("Updating your bundle…");
    }

    try {
      if (editingBindId) {
        await this._removeBindGroup(editingBindId);
        this._clearEditMode();
        this._clearBundleParamFromUrl();
      }

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(",")
        })
      });

      const responseJson = await response.json();
      if (response.ok) {
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        if (responseJson.sections) {
          cartContent.sections = responseJson.sections;
        }
        await this._refreshFromCart({ reset: true, cart: cartContent });
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent
          }
        }));
        document.querySelector("cart-drawer")?.show();
      } else {
        console.error("Mix and Match add to cart failed", responseJson);
      }
    } catch (error) {
      console.error("Mix and Match add to cart failed", error);
    } finally {
      this.atcBtn.removeAttribute("aria-busy");
      this._hidePageOverlay();
      this._render();
    }
  }
}

if (!window.customElements.get("mix-and-match-set")) {
  window.customElements.define("mix-and-match-set", MixAndMatchSet);
}
